// Dashboard del dueño/jefe de taller. Taller-Game → Resumen → Dashboard.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Banknote, Clock3, Gauge, Plus, RotateCcw, ShieldCheck, Timer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useTabsStore } from '@/stores/tabs.store';
import { ESTADOS, EstadoOrden, OrdenFila, money, tallerRpc, QK, useSort, SortTh } from './shared';

interface GarantiaTecnico {
  tecnico: string; reingresos: number; cubiertos: number; excluidos: number; costo_repuestos: number; mano_obra_no_cobrada: number;
}
interface EficienciaTecnico {
  tecnico: string; ordenes: number; minutos_reales: number; minutos_estandar: number;
}
interface TableroKpi {
  por_estado: Partial<Record<EstadoOrden, number>>;
  atrasadas: number;
  listos_sin_retirar: number;
  presupuestos_pendientes: number;
  reingresos_30d: number;
  anticipos_abiertos: number;
  garantias: GarantiaTecnico[];
  eficiencia: EficienciaTecnico[];
}

interface Columna { label: string; estados: EstadoOrden[] }
const COLUMNAS: Columna[] = [
  { label: 'Recibido', estados: ['recibido'] },
  { label: 'Diagnóstico', estados: ['diagnostico'] },
  { label: 'Presupuesto enviado', estados: ['presupuestado'] },
  { label: 'Aprobado', estados: ['aprobado', 'rechazado'] },
  { label: 'Reparación', estados: ['reparacion', 'esperando_repuesto'] },
  { label: 'Pruebas', estados: ['pruebas'] },
  { label: 'Listo', estados: ['listo'] },
];

const diasDesde = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));

export function TableroTaller() {
  const openTab = useTabsStore((s) => s.openTab);

  const { data: kpi } = useQuery<TableroKpi>({
    queryKey: [QK, 'tablero'], queryFn: () => tallerRpc<TableroKpi>('tablero'), refetchInterval: 30000,
  });
  const { data: ordenes = [] } = useQuery<OrdenFila[]>({
    queryKey: [QK, 'ordenes', 'abiertas'], queryFn: () => tallerRpc<OrdenFila[]>('ordenes_listar', { _estado: 'abiertas', _q: null }), refetchInterval: 30000,
  });

  const porColumna = useMemo(() => {
    const map = new Map<string, OrdenFila[]>();
    for (const c of COLUMNAS) map.set(c.label, []);
    for (const o of ordenes) {
      const col = COLUMNAS.find((c) => c.estados.includes(o.estado));
      if (col) map.get(col.label)!.push(o);
    }
    return map;
  }, [ordenes]);

  const abrirOrden = (o: OrdenFila) => {
    sessionStorage.setItem('taller_abrir_orden', o.id);
    openTab({ id: '/taller/ordenes', title: 'Órdenes', path: '/taller/ordenes' });
  };
  const nuevaRecepcion = () => openTab({ id: '/taller/recepcion', title: 'Nueva recepción', path: '/taller/recepcion' });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Gauge className="w-5 h-5" /> Dashboard del taller</h1>
        <Button onClick={nuevaRecepcion}><Plus className="w-4 h-4 mr-1" /> Nueva recepción</Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi icon={<Clock3 className="w-4 h-4" />} label="Presupuestos pendientes" valor={kpi?.presupuestos_pendientes ?? '—'} />
        <Kpi icon={<Gauge className="w-4 h-4" />} label="Listos sin retirar" valor={kpi?.listos_sin_retirar ?? '—'} destacar={!!kpi?.listos_sin_retirar} />
        <Kpi icon={<AlertTriangle className="w-4 h-4" />} label="Atrasadas" valor={kpi?.atrasadas ?? '—'} destacar={!!kpi?.atrasadas} peligro />
        <Kpi icon={<RotateCcw className="w-4 h-4" />} label="Reingresos (30 días)" valor={kpi?.reingresos_30d ?? '—'} />
        <Kpi icon={<Banknote className="w-4 h-4" />} label="Anticipos abiertos" valor={kpi ? money(kpi.anticipos_abiertos) : '—'} />
      </div>

      <div className="overflow-x-auto">
        <div className="flex gap-3 min-w-max pb-2">
          {COLUMNAS.map((c) => {
            const filas = porColumna.get(c.label) ?? [];
            return (
              <div key={c.label} className="w-64 shrink-0 space-y-2">
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-sm font-semibold">{c.label}</h2>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{filas.length}</span>
                </div>
                <div className="space-y-2">
                  {filas.length === 0 && <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">Sin órdenes</div>}
                  {filas.map((o) => <TarjetaOrden key={o.id} o={o} onClick={() => abrirOrden(o)} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <GarantiasTecnico filas={kpi?.garantias ?? []} />
      <EficienciaTecnico filas={kpi?.eficiencia ?? []} />
    </div>
  );
}

function GarantiasTecnico({ filas }: { filas: GarantiaTecnico[] }) {
  const sort = useSort<GarantiaTecnico>(filas, (f, k) => (k === 'tecnico' ? f.tecnico : k === 'reingresos' ? f.reingresos : k === 'cubiertos' ? f.cubiertos
    : k === 'excluidos' ? f.excluidos : k === 'costo' ? f.costo_repuestos : k === 'mano_obra' ? f.mano_obra_no_cobrada : null));
  const totales = useMemo(() => filas.reduce((a, f) => ({
    reingresos: a.reingresos + f.reingresos, cubiertos: a.cubiertos + f.cubiertos, excluidos: a.excluidos + f.excluidos,
    costo: a.costo + Number(f.costo_repuestos), mano_obra: a.mano_obra + Number(f.mano_obra_no_cobrada),
  }), { reingresos: 0, cubiertos: 0, excluidos: 0, costo: 0, mano_obra: 0 }), [filas]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4" /> Garantías por técnico (90 días)</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 text-right">N°</th>
            <SortTh col="tecnico" sort={sort}>Técnico</SortTh>
            <SortTh col="reingresos" sort={sort} align="right">Reingresos</SortTh>
            <SortTh col="cubiertos" sort={sort} align="right">Cubiertos</SortTh>
            <SortTh col="excluidos" sort={sort} align="right">Excluidos</SortTh>
            <SortTh col="costo" sort={sort} align="right">Costo repuestos</SortTh>
            <SortTh col="mano_obra" sort={sort} align="right">Mano de obra no cobrada</SortTh>
          </tr></thead>
          <tbody>
            {sort.sorted.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin reingresos en garantía en los últimos 90 días</td></tr>}
            {sort.sorted.map((f, i) => (
              <tr key={f.tecnico} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-3">{f.tecnico}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.reingresos}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.cubiertos}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.excluidos}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(f.costo_repuestos)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(f.mano_obra_no_cobrada)}</td>
              </tr>
            ))}
          </tbody>
          {filas.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
            <td colSpan={2} className="py-2 pr-3">Total: {filas.length} técnico(s)</td>
            <td className="py-2 pr-3 text-right tabular-nums">{totales.reingresos}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{totales.cubiertos}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{totales.excluidos}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{money(totales.costo)}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{money(totales.mano_obra)}</td>
          </tr></tfoot>}
        </table>
      </CardContent>
    </Card>
  );
}

function EficienciaTecnico({ filas }: { filas: EficienciaTecnico[] }) {
  const sort = useSort<EficienciaTecnico>(filas, (f, k) => (k === 'tecnico' ? f.tecnico : k === 'ordenes' ? f.ordenes
    : k === 'reales' ? f.minutos_reales : k === 'estandar' ? f.minutos_estandar
    : k === 'eficiencia' ? (f.minutos_estandar > 0 ? f.minutos_estandar / Math.max(f.minutos_reales, 1) : null) : null));
  const totales = useMemo(() => filas.reduce((a, f) => ({
    ordenes: a.ordenes + f.ordenes, reales: a.reales + Number(f.minutos_reales), estandar: a.estandar + Number(f.minutos_estandar),
  }), { ordenes: 0, reales: 0, estandar: 0 }), [filas]);
  const pct = (f: EficienciaTecnico) => (f.minutos_estandar > 0 ? `${Math.round((f.minutos_estandar / Math.max(f.minutos_reales, 1)) * 100)}%` : '—');

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><Timer className="h-4 w-4" /> Eficiencia por técnico (30 días)</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 text-right">N°</th>
            <SortTh col="tecnico" sort={sort}>Técnico</SortTh>
            <SortTh col="ordenes" sort={sort} align="right">Órdenes</SortTh>
            <SortTh col="reales" sort={sort} align="right">Minutos reales</SortTh>
            <SortTh col="estandar" sort={sort} align="right">Minutos estándar</SortTh>
            <SortTh col="eficiencia" sort={sort} align="right">Eficiencia</SortTh>
          </tr></thead>
          <tbody>
            {sort.sorted.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin órdenes terminadas en los últimos 30 días</td></tr>}
            {sort.sorted.map((f, i) => (
              <tr key={f.tecnico} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-3">{f.tecnico}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.ordenes}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.minutos_reales}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{f.minutos_estandar}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{pct(f)}</td>
              </tr>
            ))}
          </tbody>
          {filas.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
            <td colSpan={2} className="py-2 pr-3">Total: {filas.length} técnico(s)</td>
            <td className="py-2 pr-3 text-right tabular-nums">{totales.ordenes}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{totales.reales}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{totales.estandar}</td>
            <td className="py-2 pr-3 text-right tabular-nums">{totales.estandar > 0 ? `${Math.round((totales.estandar / Math.max(totales.reales, 1)) * 100)}%` : '—'}</td>
          </tr></tfoot>}
        </table>
      </CardContent>
    </Card>
  );
}

function Kpi({ icon, label, valor, destacar, peligro }: { icon: React.ReactNode; label: string; valor: string | number; destacar?: boolean; peligro?: boolean }) {
  return (
    <Card className={destacar ? (peligro ? 'border-rose-300 dark:border-rose-800' : 'border-amber-300 dark:border-amber-800') : ''}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon} {label}</div>
        <div className={`mt-1 text-xl font-semibold ${destacar ? (peligro ? 'text-rose-600' : 'text-amber-600') : ''}`}>{valor}</div>
      </CardContent>
    </Card>
  );
}

function TarjetaOrden({ o, onClick }: { o: OrdenFila; onClick: () => void }) {
  const dias = o.estado === 'listo' && o.dias_listo != null ? o.dias_listo : diasDesde(o.created_at);
  const diasLbl = o.estado === 'listo' ? `${dias}d esperando` : `${dias}d`;
  return (
    <button type="button" onClick={onClick} className="block w-full rounded-lg border bg-card p-3 text-left text-sm shadow-sm transition-all hover:shadow-md">
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-xs font-semibold">{o.codigo}</span>
        <div className="flex items-center gap-1">
          {o.prioridad === 'urgente' && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">URGENTE</span>}
          {o.atrasada && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">ATRASADA</span>}
          {o.reingreso && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">REINGRESO</span>}
        </div>
      </div>
      <div className="mt-1 truncate font-medium">{o.equipo || o.tipo}</div>
      <div className="truncate text-xs text-muted-foreground">{o.cliente}</div>
      <div className="mt-1 truncate text-xs" title={o.falla}>{o.falla}</div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{o.tecnico ?? 'Sin técnico'}</span>
        <span className={o.atrasada ? 'font-semibold text-amber-600' : ''}>{diasLbl}</span>
      </div>
      <div className="mt-1">
        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] ${ESTADOS[o.estado].cls}`}>{ESTADOS[o.estado].label}</span>
      </div>
    </button>
  );
}
